import {useState} from 'react';
import {ComponentSwitch, NewIconButton} from '@rocket-ui/components';
import {ArrowLeftIcon} from '@rocket-ui/icons';
import {useNavigate} from 'react-router-dom';
import {useSetAtom} from 'jotai';
import {useSendRecoveryCode} from '@rocket-ui/lk-api';
import {useUserInteraction} from '@rocket-ui/user-interaction';
import {tokenAtom} from 'features/auth/model/authAtom';
import {noteLoginSession} from 'features/auth/model/sessionCount';
import {type AuthResponseSource, type AuthTokenResponse, resolvePostAuthPath} from 'features/auth/model/postAuthRedirect';
import {PhoneInputForm} from './PhoneInputForm';
import {CodeInputForm} from './CodeInputForm';
import {TelegramCodeInputForm} from './TelegramCodeInputForm';
import {PasswordInputForm} from './PasswordInputForm';
import {RecoveryPasswordInputForm} from './RecoveryPasswordInputForm';
import {RecoveryPasswordCodeInputForm} from './RecoveryPasswordCodeInputForm';
import {RecoveryPasswordTelegramCodeInputForm} from './RecoveryPasswordTelegramCodeInputForm';
import {RepeatPasswordInputForm} from './RepeatPasswordInputForm';
import styles from './AuthorizationSteps.module.css';

enum Step {
	Phone,
	Code,
	TelegramCode,
	Password,
	NewPassword,
	RecoveryPasswordCode,
	RecoveryPasswordTelegramCode,
	RecoveryPassword,
	RepeatPassword,
}

export const AuthorizationSteps = () => {
	const navigate = useNavigate();
	const setTokenDetails = useSetAtom(tokenAtom);
	const {notifyUser} = useUserInteraction();
	const {mutateAsync: sendRecoveryCode, isLoading: isSendingRecoveryCode} = useSendRecoveryCode();
	const [step, setStep] = useState(Step.Phone);
	const [phone, setPhone] = useState('');
	const [codeValidationToken, setCodeValidationToken] = useState('');
	const [codeEntryKind, setCodeEntryKind] = useState<'sms' | 'telegram' | null>(null);
	const [usedOtpStrategy, setUsedOtpStrategy] = useState<string | null>(null);
	const [passwordValidationToken, setPasswordValidationToken] = useState('');
	const [recoveryCodeValidationToken, setRecoveryCodeValidationToken] = useState('');
	const [secretCodeToken, setSecretCodeToken] = useState('');
	const [recoveryPassword, setRecoveryPassword] = useState('');
	const [passwordSetupFromStep, setPasswordSetupFromStep] = useState(Step.NewPassword);

	const isTelegramOtpStrategy = usedOtpStrategy === 'telegram_provider';

	const handleBack = () => {
		switch (step) {
			case Step.Code:
			case Step.TelegramCode:
				setCodeEntryKind(null);
				setStep(Step.Phone);
				break;
			case Step.Password:
				setStep(codeEntryKind === 'telegram' ? Step.TelegramCode : Step.Code);
				break;
			case Step.NewPassword:
				setStep(codeEntryKind === 'telegram' ? Step.TelegramCode : Step.Code);
				break;
			case Step.RecoveryPasswordCode:
			case Step.RecoveryPasswordTelegramCode:
				setStep(Step.Password);
				break;
			case Step.RecoveryPassword:
				setStep(isTelegramOtpStrategy ? Step.RecoveryPasswordTelegramCode : Step.RecoveryPasswordCode);
				break;
			case Step.RepeatPassword:
				setStep(passwordSetupFromStep);
				break;
		}
	};

	const handlePhoneSubmit = (phone: string, token: string, strategy?: string | null) => {
		setPhone(phone);
		setCodeValidationToken(token);
		setUsedOtpStrategy(strategy ?? null);
		if (strategy === 'telegram_provider') {
			setCodeEntryKind('telegram');
			setStep(Step.TelegramCode);
		} else {
			setCodeEntryKind('sms');
			setStep(Step.Code);
		}
	};

	const handleCodeSubmit = (token: string, clientStatus?: string) => {
		setPasswordValidationToken(token);

		if (clientStatus === 'need_secret_code') {
			setSecretCodeToken(token);
			setPasswordSetupFromStep(Step.NewPassword);
			setStep(Step.NewPassword);
			return;
		}

		setStep(Step.Password);
	};

	const handleForgotPassword = async () => {
		try {
			const data = await sendRecoveryCode({
				token: passwordValidationToken,
				otp_strategy: usedOtpStrategy,
			});
			setRecoveryCodeValidationToken(data.authToken);
			setRecoveryPassword('');
			setPasswordSetupFromStep(Step.RecoveryPassword);
			setStep(isTelegramOtpStrategy ? Step.RecoveryPasswordTelegramCode : Step.RecoveryPasswordCode);
		} catch {
			notifyUser('Ошибка при отправке кода');
		}
	};

	const handleRecoveryCodeSubmit = (token: string) => {
		setSecretCodeToken(token);
		setStep(Step.RecoveryPassword);
	};

	const handleNewPasswordSubmit = (password: string) => {
		setRecoveryPassword(password);
		setStep(Step.RepeatPassword);
	};

	const handleLogin = (response: AuthTokenResponse, source: AuthResponseSource) => {
		noteLoginSession();
		setTokenDetails({token: response.authToken, expiresAt: response.expiresAt});
		navigate(resolvePostAuthPath(response, source), {replace: true});
	};

	return (
		<>
			<div className={styles.backWrap}>
				{step !== Step.Phone && <NewIconButton icon={ArrowLeftIcon} onClick={handleBack} round />}
			</div>
			<div className={styles.main}>
				<ComponentSwitch value={step}>
					<ComponentSwitch.Case statement={Step.Phone}>
						<PhoneInputForm onSubmit={handlePhoneSubmit} />
					</ComponentSwitch.Case>
					<ComponentSwitch.Case statement={Step.Code}>
						<CodeInputForm phone={phone} token={codeValidationToken} onSubmit={handleCodeSubmit} />
					</ComponentSwitch.Case>
					<ComponentSwitch.Case statement={Step.TelegramCode}>
						<TelegramCodeInputForm phone={phone} token={codeValidationToken} onSubmit={handleCodeSubmit} />
					</ComponentSwitch.Case>
					<ComponentSwitch.Case statement={Step.Password}>
						<PasswordInputForm
							token={passwordValidationToken}
							onForgotPasswordClick={handleForgotPassword}
							isForgotPasswordLoading={isSendingRecoveryCode}
							onSubmit={handleLogin}
						/>
					</ComponentSwitch.Case>
					<ComponentSwitch.Case statement={Step.NewPassword}>
						<RecoveryPasswordInputForm
							token={passwordValidationToken}
							secretCodeEndpoint="auth"
							onSubmit={handleNewPasswordSubmit}
						/>
					</ComponentSwitch.Case>
					<ComponentSwitch.Case statement={Step.RecoveryPasswordCode}>
						<RecoveryPasswordCodeInputForm
							phone={phone}
							token={recoveryCodeValidationToken}
							onSubmit={handleRecoveryCodeSubmit}
						/>
					</ComponentSwitch.Case>
					<ComponentSwitch.Case statement={Step.RecoveryPasswordTelegramCode}>
						<RecoveryPasswordTelegramCodeInputForm
							phone={phone}
							token={recoveryCodeValidationToken}
							onSubmit={handleRecoveryCodeSubmit}
						/>
					</ComponentSwitch.Case>
					<ComponentSwitch.Case statement={Step.RecoveryPassword}>
						<RecoveryPasswordInputForm
							token={secretCodeToken}
							secretCodeEndpoint="recovery"
							onSubmit={handleNewPasswordSubmit}
						/>
					</ComponentSwitch.Case>
					<ComponentSwitch.Case statement={Step.RepeatPassword}>
						<RepeatPasswordInputForm
							token={
								passwordSetupFromStep === Step.RecoveryPassword
									? secretCodeToken
									: passwordValidationToken
							}
							password={recoveryPassword}
							confirmWith={passwordSetupFromStep === Step.RecoveryPassword ? 'recovery' : 'validate'}
							onSubmit={handleLogin}
						/>
					</ComponentSwitch.Case>
				</ComponentSwitch>
			</div>
		</>
	);
};
